import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, MessageSquare, Users, Wallet, Send, IdCard,
  Key, LogOut, Menu, X, ChevronDown, ChevronRight,
  Settings, BarChart3, Star, Zap, ShoppingBag, Cake, Users2, CreditCard
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { NotificationBell } from "@/components/NotificationBell";
import { SEO } from "@/components/SEO";

const NAV = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
  {
    icon: MessageSquare, label: "SMS", children: [
      { label: "Compose SMS", path: "/dashboard/compose" },
      { label: "Campaigns", path: "/dashboard/campaigns" },
      { label: "Sent Messages", path: "/dashboard/sent" },
    ]
  },
  {
    icon: Users, label: "Contacts", children: [
      { label: "Phonebook", path: "/dashboard/contacts" },
    ]
  },
  {
    icon: Wallet, label: "Wallet", children: [
      { label: "Balance & History", path: "/dashboard/wallet" },
      { label: "Subscribe", path: "/dashboard/subscribe" },
      { label: "Top-Up", path: "/dashboard/topup" },
    ]
  },
  { icon: BarChart3, label: "Analytics", path: "/dashboard/analytics" },
  { icon: ShoppingBag, label: "Marketplace", path: "/dashboard/marketplace" },
  { icon: Cake, label: "Birthday SMS", path: "/dashboard/birthday" },
  { icon: Users2, label: "Team", path: "/dashboard/team" },
  { icon: IdCard, label: "Sender IDs", path: "/dashboard/sender-ids" },
  { icon: Key, label: "API Keys", path: "/dashboard/api-keys" },
  { icon: Settings, label: "Profile", path: "/dashboard/profile" },
];

function NavItem({ item, onClose }) {
  const [open, setOpen] = useState(false);
  if (item.children) {
    return (
      <div>
        <button
          onClick={() => setOpen(!open)}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-all"
        >
          <item.icon size={17} />
          <span className="flex-1 text-left">{item.label}</span>
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        {open && (
          <div className="ml-8 mt-0.5 space-y-0.5">
            {item.children.map((child) => (
              <NavLink
                key={child.path}
                to={child.path}
                onClick={onClose}
                className={({ isActive }) =>
                  `block px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    isActive
                      ? "text-[#0c1e41] bg-[#eef1f8] font-semibold"
                      : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                  }`
                }
              >
                {child.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }
  return (
    <NavLink
      to={item.path}
      end={item.path === "/dashboard"}
      onClick={onClose}
      className={({ isActive }) =>
        isActive ? "nav-item-active" : "nav-item-inactive"
      }
    >
      <item.icon size={17} />
      {item.label}
    </NavLink>
  );
}

export default function CustomerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out");
    navigate("/");
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-100">
        <img src="/sendora-logo.webp" alt="Sendora" className="h-7 w-auto" />
      </div>
      <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
        {NAV.map((item) => (
          <NavItem key={item.label} item={item} onClose={() => setSidebarOpen(false)} />
        ))}
      </nav>
      <div className="border-t border-slate-100 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-[#091629] flex items-center justify-center text-white text-sm font-bold shadow-sm">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          variant="ghost" size="sm"
          className="w-full justify-start text-slate-500 hover:text-red-600 hover:bg-red-50 h-8 text-xs"
          onClick={handleLogout}
          data-testid="logout-button"
        >
          <LogOut size={14} className="mr-2" /> Sign Out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#FAFAFC] overflow-hidden">
      <SEO noindex title="Dashboard" canonical="/dashboard" />
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200 shrink-0 shadow-sm">
        <SidebarContent />
      </aside>
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <aside className="relative flex flex-col w-64 bg-white border-r border-slate-200 shadow-xl">
            <button className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-1" onClick={() => setSidebarOpen(false)}>
              <X size={20} />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 shrink-0 shadow-sm">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg" onClick={() => setSidebarOpen(true)}>
              <Menu size={20} />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              variant="outline"
              className="border-[#0c1e41] text-[#0c1e41] hover:bg-[#eef1f8] hidden sm:flex h-8 text-xs"
              onClick={() => navigate("/dashboard/topup")}
              data-testid="customer-topup-button"
            >
              <CreditCard size={13} className="mr-1.5" /> Top-Up
            </Button>
            <Button
              size="sm"
              className="bg-[#0c1e41] hover:bg-[#091629] text-white hidden sm:flex h-8 text-xs shadow-sm"
              onClick={() => navigate("/dashboard/compose")}
              data-testid="customer-compose-sms-button"
            >
              <Send size={13} className="mr-1.5" /> Send SMS
            </Button>
            <NotificationBell />
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-[#091629] flex items-center justify-center text-white text-sm font-bold shadow-sm">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="page-enter"><Outlet /></div>
        </main>
      </div>
    </div>
  );
}
